// MainLayout.jsx (MUI-only, responsive, role-aware)
// Notes: Keeps your fetch + contexts logic; replaces Bootstrap/FA with MUI

import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Divider,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  useMediaQuery,
  Button,
} from "@mui/material";
import { styled, useTheme, createTheme, ThemeProvider } from "@mui/material/styles";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SmsIcon from "@mui/icons-material/Sms";
import CampaignIcon from "@mui/icons-material/Campaign";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import DescriptionIcon from "@mui/icons-material/Description";
import SavingsIcon from "@mui/icons-material/Savings";
import PaidIcon from "@mui/icons-material/Paid";
import ShieldIcon from "@mui/icons-material/Shield"; // Admin section marker
import GavelIcon from "@mui/icons-material/Gavel"; // Approvals
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism"; // Special, Dividends
import ReportProblemIcon from "@mui/icons-material/ReportProblem"; // Penalties
import PersonOffIcon from "@mui/icons-material/PersonOff"; // Deactivated
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert"; // Emergency
import InfoIcon from "@mui/icons-material/Info"; // General
import AddCardIcon from "@mui/icons-material/AddCard"; // Contributions
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

// Project deps you already have
import { domain } from "../auth/domain";
import { useBranch } from "../contexts/BranchContext";
import { useLoanNotification } from "../contexts/LoanNotificationContext";
import { useExpensesNotification } from "../contexts/ExpensesNotificationContext";
import { useDividendNotification } from "../contexts/DividendNotificationContext";
import { usePenaltyNotification } from "../contexts/PenaltyNotificationContext";
import { useTransactionNotification } from "../contexts/TransactionNotificationContext";
import { useAuth } from "../contexts/AuthContext";
import { AnnouncementModal } from "../pages/Dashboard";
import LanguageSwitcher from "../components/LanguageSwitcher";
import savvyLogoTransparent from "../assets/img/savvy-transparent-logo.png";

// ---- Brand theming
const brand = "#800b33";
const brandHover = "#6c0a2b";
const drawerWidth = 270;

// Optional: global theme injection (keeps your brand as primary)
const baseTheme = createTheme({
  palette: {
    primary: { main: brand },
    secondary: { main: "#0d47a1" },
  },
  shape: { borderRadius: 10 },
});

// Styled helpers
const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  minHeight: "100vh",
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 2),
  gap: 12,
}));

// A clean NavLink wrapper for MUI ListItemButton
const NavItem = ({ to, icon, label, active, badge = 0, hidden = false }) => {
  if (hidden) {
    return null;
  }
  return (
    <ListItemButton
      component={NavLink}
      to={to}
      selected={active}
      sx={(theme) => ({
        borderRadius: 1,
        mb: 0.5,
        "&.active": {
          backgroundColor: theme.palette.action.selected,
        },
      })}
    >
      {!!icon && <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>}
      <ListItemText primary={label} />
      {badge > 0 && (
        <Chip size="small" label={badge} color="error" sx={{ height: 20, "& .MuiChip-label": { px: 0.5 } }} />
      )}
    </ListItemButton>
  );
};

function MainLayout({ onLogout, activePage, logoSrc }) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const systemName = t("layout.systemName");


  const [mobileOpen, setMobileOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  // Contexts
  const { branch, setBranch } = useBranch();
  const { loanNotification, setLoanNotification } = useLoanNotification();
  const { dividendNotification, setDividendNotification } = useDividendNotification();
  const { penaltyNotification, setPenaltyNotification } = usePenaltyNotification();
  const { transactionNotification, setTransactionNotification } = useTransactionNotification();
  const { expensesNotification, setExpensesNotification } = useExpensesNotification();
  const { user: authUser, logout } = useAuth();

  // Data state
  const [branches, setBranches] = useState([]);
  const [notifications, setNotifications] = useState([]); // For generic notifications
  const [announcementNotifications, setAnnouncementNotifications] = useState([]); // For announcements
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  // Menus
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorUser, setAnchorUser] = useState(null);

  // Announcement modal
  const [openAnnouncement, setOpenAnnouncement] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const groupDisplayName = branch?.name || authUser?.group_name || t("layout.noGroup");
  const canSwitchGroup = (authUser?.memberships_count || 0) > 1;

  // ====== Fetching helpers (kept from your logic, adapted) ======

  // New generic function to fetch all notifications for the user
  const fetchAllUserNotifications = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(domain + "notifications/notifications/", {
      headers: { Authorization: "JWT " + token },
    })
      .then((r) => r.json())
      .then((data) => {
        // Filter for unread notifications
        const unread = data.filter((n) => !n.is_read);
        setNotifications(unread); // Store all unread notifications
        setUnreadNotificationsCount(unread.length); // Total unread count for the main bell

        // Update specific badges based on type
        const loanNotifs = unread.filter((n) => n.type === 'LOAN_APPROVAL');
        setLoanNotification(loanNotifs.length);

        // Assuming 'EXPENSE_APPROVAL' or similar type for expenses
        // You need to ensure your backend creates notifications with type 'EXPENSE_APPROVAL'
        const expenseNotifs = unread.filter((n) => n.type === 'EXPENSE_APPROVAL');
        setExpensesNotification(expenseNotifs.length);

        const dividendNotifs = unread.filter((n) => n.type === 'DIVIDEND_PAYOUT');
        setDividendNotification(dividendNotifs.length);

        const penaltyNotifs = unread.filter((n) => n.type === 'PENALTY_APPLIED');
        setPenaltyNotification(penaltyNotifs.length);

        const transactionNotifs = unread.filter((n) => n.type === 'TRANSACTION_ALERT');
        setTransactionNotification(transactionNotifs.length);

        // If there are other types of notifications that need specific badges, add them here
      })
      .catch((error) => console.error("Error fetching user notifications:", error));
  };

  const fetchBranches = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(domain + "branches/branches/", {
      headers: { Authorization: "JWT " + token },
    })
      .then((r) => r.json())
      .then((data) => setBranches(data))
      .catch(() => { });
  };

  const fetchAnnouncement = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(domain + "announcements/announcement-read-status/", {
      headers: { Authorization: "JWT " + token },
    })
      .then((r) => r.json())
      .then((data) => {
        const nowValid = data.filter((n) => new Date(n.announcement.is_active_till) >= new Date()); // Keep this logic
        setAnnouncementNotifications(nowValid); // Use the dedicated state
        const unread = nowValid.filter((n) => !n.is_read).length; // This is correct for announcements
        setUnreadNotificationsCount(unread);
      })
      .catch(() => { });
  };

  useEffect(() => {
    fetchBranches(); // This is still needed for the branch picker
    fetchAllUserNotifications(); // Use the new generic fetch for all notifications
    fetchAnnouncement();
    // eslint-disable-next-line
  }, [authUser]);

  // Reset notifications when user visits the relevant page
  // And inform the backend to mark them as read
  const markNotificationsByType = (type) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    fetch(domain + "notifications/notifications/mark-type-as-read/", {
      method: "POST",
      headers: {
        Authorization: "JWT " + token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ type }),
    })
      .then((response) => {
        if (!response.ok) {
          console.error("Failed to mark notifications as read for type:", type);
        }
      })
      .then(() => fetchAllUserNotifications())
      .catch((error) => console.error("Error marking notifications by type:", error));
  };

  useEffect(() => {
    if (location.pathname === "/loan-approvals") {
      // Client-side reset for immediate UI update
      setLoanNotification(0);

      // API call to backend to mark loan notifications as read
      fetch(domain + "loans/loans/mark-notifications-as-read/", {
        method: "POST",
        headers: {
          Authorization: "JWT " + localStorage.getItem("accessToken"),
          "Content-Type": "application/json",
        },
        // Optionally send branch_id if the backend needs it to filter notifications
        body: JSON.stringify({ branch_id: branch?.id }),
      })
        .then((response) => {
          if (!response.ok) {
            console.error("Failed to mark loan notifications as read on backend.");
          }
          // After marking as read on backend, refresh all notifications to update general count
          fetchAllUserNotifications();
        }).catch((error) => console.error("Error marking loan notifications as read:", error));
    }
    if (location.pathname === "/member/dividends") {
      setDividendNotification(0);
      markNotificationsByType("DIVIDEND_PAYOUT");
    }
    if (location.pathname === "/member/contributions") {
      setPenaltyNotification(0);
      markNotificationsByType("PENALTY_APPLIED");
    }
    if (location.pathname === "/finance/transactions-audit") {
      setTransactionNotification(0);
      markNotificationsByType("TRANSACTION_ALERT");
    }

    // Add similar logic for expenses if needed, assuming an 'EXPENSE_APPROVAL' type
    // and a corresponding backend endpoint.
    // if (location.pathname === "/expenses-approvals") { // Assuming such a path exists
    //   setExpensesNotification(0);
    //   fetch(domain + "expenses/mark-notifications-as-read/", { // Assuming such an endpoint exists
    //     method: "POST",
    //     headers: { Authorization: "JWT " + localStorage.getItem("accessToken"), "Content-Type": "application/json" },
    //     body: JSON.stringify({ branch_id: branch?.id }),
    //   }).then((response) => {
    //     if (!response.ok) console.error("Failed to mark expense notifications as read.");
    //     else fetchAllUserNotifications();
    //   }).catch((error) => console.error("Error marking expense notifications as read:", error));
    // }
  }, [location.pathname, setLoanNotification, setExpensesNotification, setDividendNotification, setPenaltyNotification, setTransactionNotification, authUser, branch?.id]);

  // ====== UI Handlers ======
  const toggleDrawer = () => setMobileOpen((p) => !p);
  const openNotifMenu = (e) => setAnchorNotif(e.currentTarget);
  const closeNotifMenu = () => setAnchorNotif(null);
  const openUserMenu = (e) => setAnchorUser(e.currentTarget);
  const closeUserMenu = () => setAnchorUser(null);

  // Helper to get an icon for a notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "LOAN_APPROVAL":
        return <GavelIcon fontSize="small" color="primary" />;
      case "PENALTY_APPLIED":
      case "ACCOUNT_DEACTIVATED":
        return <ReportProblemIcon fontSize="small" color="error" />;
      case "EMERGENCY_CONTRIBUTION_INVOICE":
        return <CrisisAlertIcon fontSize="small" color="warning" />;
      case "LOAN_REPAYMENT":
        return <PaidIcon fontSize="small" color="action" />;
      case "DIVIDEND_PAYOUT":
        return <VolunteerActivismIcon fontSize="small" color="secondary" />;
      case "TRANSACTION_ALERT":
        return <AccountBalanceIcon fontSize="small" color="primary" />;
      default:
        return <InfoIcon fontSize="small" color="disabled" />;
    }
  };
  // Generic handler for clicking on a notification in the dropdown
  const handleNotificationClick = (notification) => {
    closeNotifMenu(); // Close the notification menu

    // Mark the specific notification as read on the backend
    fetch(domain + `notifications/notifications/${notification.id}/`, {
      method: "PATCH", // Use PATCH to update a specific field
      headers: {
        Authorization: "JWT " + localStorage.getItem("accessToken"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ is_read: true }),
    })
      .then((response) => {
        if (!response.ok) console.error("Failed to mark notification as read on backend.");
        else fetchAllUserNotifications(); // Refresh all notifications to update counts and list
      })
      .then(() => {
        // Navigate to the relevant page after marking as read
        if (notification.type === 'LOAN_APPROVAL') {
          navigate(withAppPath('/loan-approvals'));
        } else if (notification.type === 'DIVIDEND_PAYOUT') {
          navigate(withAppPath('/member/dividends'));
        } else if (notification.type === 'PENALTY_APPLIED') {
          navigate(withAppPath('/member/contributions'));
        } else if (notification.type === 'TRANSACTION_ALERT') {
          navigate(withAppPath('/finance/transactions-audit'));
        }
        // Add other navigation logic here for other types
        // e.g., if (notification.type === 'PENALTY_APPLIED') navigate('/my-penalties');
      })
      .catch((error) => console.error("Error marking notification as read:", error));
  };

  const handleAnnouncementModal = (notification) => {
    setSelectedAnnouncement(notification.announcement);
    setOpenAnnouncement(true);

    fetch(domain + `announcements/announcements/${notification?.announcement?.id}/mark_as_read/`, {
      method: "POST",
      headers: { Authorization: "JWT " + localStorage.getItem("accessToken") },
    })
      .then(() => fetchAnnouncement())
      .catch(() => { });
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure you want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand,
      confirmButtonText: "Logout",
    }).then((res) => {
      if (res.isConfirmed) {
        logout();
        navigate("/login", { replace: true });
      }
    });
  };

  // ====== Role awareness (optional filter in future) ======
  const role = authUser?.role || "User";

  // ====== Nav Model (central place for routes) ======
  const locationKey = location.pathname;
  const appBase = "/app";
  const withAppPath = (path) => {
    if (!path) return path;
    if (path.startsWith(appBase)) return path;
    return `${appBase}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const adminSection = [
    { labelKey: "nav.adminDashboard", to: "/admin/dashboard", icon: <DashboardIcon />, hidden: !["Admin"].includes(role) },
    { labelKey: "nav.userManagementAdmin", to: "/admin/users", icon: <ShieldIcon />, hidden: !["Admin"].includes(role) },
    { labelKey: "nav.migrationStudio", to: "/admin/migrations", icon: <SystemUpdateAltIcon />, hidden: !["Admin"].includes(role) },
    { labelKey: "nav.adminSettings", to: "/admin/settings", icon: <SettingsIcon />, hidden: !["Admin"].includes(role) },
    { labelKey: "All Groups", to: "/admin/groups", icon: <GroupIcon />, hidden: !["Admin"].includes(role) },
    { labelKey: "Switch Active Group", to: "/my-groups", icon: <SyncAltIcon />, hidden: !["Admin"].includes(role) },
    { labelKey: "nav.chairpersonSettings", to: "/chairperson/system-settings", icon: <SettingsIcon />, hidden: !["Mwenyekiti"].includes(role) },
    { labelKey: "nav.adminReconciliation", to: "/admin/reconciliation", icon: <CompareArrowsIcon />, hidden: !["Admin", "Mwenyekiti", "Chairperson"].includes(role) },

  ].map((item) => ({ ...item, to: withAppPath(item.to) }));

  const genericDashboards = [
    { labelKey: "nav.deskOfficerDashboard", to: "/deskofficer/dashboard", icon: <DashboardIcon />, hidden: !["Desk Officer"].includes(role) },
    { labelKey: "nav.chairpersonDashboard", to: "/chairperson/dashboard", icon: <DashboardIcon />, hidden: !["Mwenyekiti"].includes(role) },
    { labelKey: "nav.katibuDashboard", to: "/katibu/dashboard", icon: <DashboardIcon />, hidden: !["Katibu"].includes(role) },
    { labelKey: "nav.accountantDashboard", to: "/accountant/dashboard", icon: <DashboardIcon />, hidden: !["Muhasibu"].includes(role) },
    { labelKey: "nav.memberDashboard", to: "/member/dashboard", icon: <DashboardIcon />, hidden: ["Admin", "Desk Officer"].includes(role) },
    { labelKey: "Entryfee", to: "/entry-fee/invoice", icon: <DashboardIcon />, hidden: ["Admin", "Desk Officer"].includes(role) },
  ].map((item) => ({ ...item, to: withAppPath(item.to) }));

  const coreRoutes = [

    { labelKey: "nav.loans", to: "/loan-approvals", icon: <PaidIcon />, badge: !["Admin", "Desk Officer"].includes(role) ? loanNotification : 0 },
  ]
    .filter((x) => !x.hidden)
    .map((item) => ({ ...item, to: withAppPath(item.to) }));

  const roleFlows = [
    { labelKey: "nav.groupsDeskOfficer", to: "/desk-officer/groups", icon: <GroupIcon />, hidden: !["Desk Officer", "Admin", "Mwenyekiti"].includes(role) },
    { labelKey: "nav.membersChairperson", to: "/chairperson/members", icon: <PeopleIcon />, },
    { labelKey: "nav.expenses", to: "/expenses", icon: <ReceiptLongIcon />, badge: expensesNotification, hidden: ["Desk Officer", "Admin"].includes(role) },

    {
      labelKey: "nav.contributionsMember",
      to: "/member/contributions",
      icon: <AddCardIcon />,
      badge: role === "Mwanachama" ? penaltyNotification : 0,
    },
    { labelKey: "nav.catchupPayments", to: "/member/catchup-payments", icon: <SavingsIcon /> },
    { labelKey: "nav.catchupPaymentsChair", to: "/chairperson/catchup-payments", icon: <SavingsIcon />, },
    { labelKey: "nav.emergencyChair", to: "/chairperson/emergency-contributions", icon: <CrisisAlertIcon />, hidden: role !== "Mwenyekiti" },
    { labelKey: "nav.emergencyMember", to: "/member/emergency-contributions", icon: <CrisisAlertIcon /> },
    { labelKey: "nav.groupBusinessesChair", to: "/chairperson/group-businesses", icon: <GroupWorkIcon /> },
    { labelKey: "nav.groupBusinessesAdmin", to: "/admin/group-businesses", icon: <GroupWorkIcon />, hidden: role !== "Admin" },
    { labelKey: "nav.specialContributionsMember", to: "/member/special-contributions", icon: <VolunteerActivismIcon /> },
    { labelKey: "nav.dividendsMember", to: "/member/dividends", icon: <PaidIcon />, badge: dividendNotification },
    {
      labelKey: "nav.financeTransactions",
      to: "/finance/transactions-audit",
      icon: <VolunteerActivismIcon />,
      badge: ["Mwenyekiti", "Katibu", "Muhasibu"].includes(role) ? transactionNotification : 0,

    },
    {
      labelKey: "nav.upatuDisbursements",
      to: "/upatu/disbursements",
      icon: <SyncAltIcon />,
      hidden: !["Mwenyekiti", "Katibu", "Muhasibu"].includes(role),
    },
    {
      labelKey: "nav.memberWithdrawalApprovals",
      to: "/leadership/member-withdrawals",
      icon: <PersonOffIcon />,
      hidden: !["Mwenyekiti", "Katibu", "Muhasibu"].includes(role),
    },
  ]
    .filter((x) => !x.hidden)
    .map((item) => ({ ...item, to: withAppPath(item.to) }));

  const reportsGroup = [
    { labelKey: "nav.monthlyGroupPerformance", to: "/reports/monthly-group-performance" },
    { labelKey: "nav.memberHealthScore", to: "/reports/member-health-score" },
    { labelKey: "nav.groupLiquidity", to: "/reports/group-liquidity" },
    {
      labelKey: "nav.systemGrowthMetrics",
      to: "/reports/system-growth-metrics",
      hidden: !["Admin", "Desk Officer"].includes(role),
    },
    // { labelKey: "nav.auditCompliance", to: "/reports/audit-compliance", hidden: role !== "Admin" },
  ]
    .filter((x) => !x.hidden)
    .map((item) => ({ ...item, to: withAppPath(item.to) }));


  // Drawer content
  const drawer = (
    <Box sx={{ width: drawerWidth, display: "flex", flexDirection: "column", height: "100%" }}>
      <DrawerHeader>
        <Box
          component="img"
          src={logoSrc || savvyLogoTransparent}
          alt={`${systemName} logo`}
          sx={{ width: 140, height: 34, objectFit: "contain" }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <IconButton color="inherit" edge="end" onClick={toggleDrawer} aria-label={t("layout.menu")}>
          <MenuIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />

      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {role}
        </Typography>
        <List dense>
          {genericDashboards.map((it) => (
            <NavItem
              key={it.to}
              to={it.to}
              icon={it.icon}
              label={t(it.labelKey)}
              active={locationKey === it.to}
              hidden={it.hidden}
            />
          ))}
        </List>
      </Box>

      <Divider />

      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {t("layout.coreSection")}
        </Typography>
        <List dense>
          {coreRoutes.map((it) => (
            <ListItemButton
              key={it.to}
              component={NavLink}
              to={it.to}
              selected={locationKey === it.to}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{it.icon}</ListItemIcon>
              <ListItemText primary={t(it.labelKey)} />
              {it.badge > 0 && (
                <Chip size="small" label={it.badge} color="error" sx={{ height: 20, "& .MuiChip-label": { px: 0.5 } }} />
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Divider />

      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="overline" color="text.secondary">
          {t("layout.workflowsSection")}
        </Typography>
        <List dense>
          {roleFlows.map((it) => (
            <NavItem
              key={it.to}
              to={it.to}
              icon={it.icon}
              label={t(it.labelKey)}
              active={locationKey === it.to}
              badge={it.badge || 0}
            />
          ))}
        </List>
      </Box>

      <Divider />

      <Box sx={{ px: 1.5, py: 1 }}>
        <List dense>
          <ListItemButton onClick={() => setReportsOpen((p) => !p)} sx={{ borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText primary={t("layout.reportsSection")} />
            {reportsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
            <List dense sx={{ pl: 4 }}>
              {reportsGroup.map((r) => (
                <NavItem key={r.to} to={r.to} label={t(r.labelKey)} active={locationKey === r.to} />
              ))}
            </List>
          </Collapse>
        </List>
      </Box>

      {/* {["Admin", "General Manager"].includes(role) && ( */}
      <>
        <Divider />
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {t("layout.adminSection")}
          </Typography>
          <List dense>
            {adminSection.map((item) => (
              <NavItem key={item.to} {...item} label={t(item.labelKey)} active={locationKey === item.to} />
            ))}
          </List>
        </Box>
      </>
      {/* )} */}

      <Box sx={{ flexGrow: 1 }} />

      {/* Footer */}
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            © {new Date().getFullYear()}
          </Typography>
          <Box
            component="img"
            src={logoSrc || savvyLogoTransparent}
            alt={systemName}
            sx={{ height: 18, width: 78, objectFit: "contain", opacity: 0.75 }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={baseTheme}>

      <Box sx={{ display: "flex" }}>

        <CssBaseline />
        {/* Top AppBar */}

        <AppBar position="fixed" elevation={1} sx={{ bgcolor: brand }}>

          <Toolbar sx={{ pr: 1 }}>
            {!mdUp && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleDrawer}
                sx={(theme) => ({
                  mr: 1,
                  position: "relative",
                  zIndex: theme.zIndex.drawer + 2,
                })}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0.5,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                <Typography variant="caption" sx={{ letterSpacing: 0.5, textTransform: "uppercase" }}>
                  {t("layout.groupLabel")}
                </Typography>
                <Chip
                  size="small"
                  icon={<GroupIcon fontSize="small" />}
                  label={groupDisplayName}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    "& .MuiChip-icon": { color: "#fff" },
                    fontWeight: 600,
                    flexShrink: 0,
                    minWidth: 140,
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 0.75,
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                <GroupIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                  {groupDisplayName}
                </Typography>
              </Box>
            </Box>
            {/*             
            <Box sx={{ display: "flex", alignItems: "center", mr: '83rem' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#fff", textTransform: "uppercase" }}>
                {groupDisplayName}
              </Typography>
            </Box> */}
            <LanguageSwitcher />
            {/* Notifications */}
            <Tooltip title={t("layout.notificationsTitle")}>
              <IconButton color="inherit" onClick={openNotifMenu}>
                <Badge badgeContent={unreadNotificationsCount} color="error">
                  <NotificationsIcon /> {/* This badge now reflects all unread generic notifications */}
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorNotif}
              open={Boolean(anchorNotif)}
              onClose={closeNotifMenu}
              PaperProps={{ sx: { width: 360 } }}
            >
              <Box sx={{ px: 2.5, pt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t("layout.notificationsTitle")}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              {notifications.length === 0 && announcementNotifications.length === 0 && ( // Check both lists
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {t("layout.noNotifications")}
                  </Typography>
                </MenuItem>
              )}
              {/* Display generic notifications first */}
              {notifications.map((n) => ( // Iterate over the generic notifications
                <MenuItem
                  key={n.id}
                  onClick={() => handleNotificationClick(n)} // Use the generic handler
                  sx={{ alignItems: "flex-start", gap: 1 }}
                >
                  {getNotificationIcon(n.type)}
                  <Box>
                    <Typography variant="body2">{n.message}</Typography>
                    {n.audience_role && (
                      <Typography variant="caption" color="text.secondary">
                        For: {n.audience_role}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(n.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
              {/* Divider if both types of notifications exist */}
              {notifications.length > 0 && announcementNotifications.length > 0 && <Divider sx={{ my: 1 }} />}
              {/* Display announcements */}
              {announcementNotifications.map((n) => (
                <MenuItem
                  key={`ann-${n.id}`}
                  onClick={() => {
                    closeNotifMenu();
                    handleAnnouncementModal(n);
                  }}
                  sx={{ alignItems: "flex-start", gap: 1 }}
                >
                  <CampaignIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{n?.announcement?.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Active till: {new Date(n?.announcement?.is_active_till).toLocaleDateString()}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>

            {/* User */}
            <Tooltip title="Account">
              <IconButton color="inherit" onClick={openUserMenu} sx={{ ml: 1 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: "#fff", color: brand, fontWeight: 700 }}>
                  {authUser?.username?.[0]?.toUpperCase() || authUser?.email?.[0]?.toUpperCase() || "U"}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorUser} open={Boolean(anchorUser)} onClose={closeUserMenu}>
              <MenuItem
                onClick={() => {
                  closeUserMenu();
                  navigate(withAppPath("/user-profile"));
                }}
              >
                <Avatar sx={{ width: 24, height: 24, mr: 1 }} /> {t("layout.profile")}
              </MenuItem>
              <Divider />
              {canSwitchGroup && (
                <MenuItem
                  onClick={() => {
                    closeUserMenu();
                    navigate(withAppPath("/my-groups"));
                  }}
                >
                  <SyncAltIcon fontSize="small" sx={{ mr: 1 }} />
                  {t("nav.switchGroup")}
                </MenuItem>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  closeUserMenu();
                  handleLogout();
                }}
                sx={{ color: "error.main" }}
              >
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                {t("layout.logout")}
              </MenuItem>
            </Menu>
          </Toolbar>

        </AppBar>

        {/* Side Drawer */}
        <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="navigation">
          {/* Temporary drawer on mobile */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={toggleDrawer}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" },
            }}
          >
            {drawer}
          </Drawer>

          {/* Permanent drawer on md+ */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main content */}
        <Main>
          <DrawerHeader />
          <Box sx={{ maxWidth: 1600, mx: "auto" }}>
            {/* active page from router parent */}
            {activePage}
          </Box>
        </Main>
      </Box>

      {/* Announcement Modal (keeps your existing component) */}
      {selectedAnnouncement && (
        <AnnouncementModal
          open={openAnnouncement}
          handleClose={() => setOpenAnnouncement(false)}
          announcement={selectedAnnouncement}
        />
      )}
    </ThemeProvider>
  );
}

export default function MainLayoutWithTheme(props) {
  return (
    <ThemeProvider theme={baseTheme}>
      <MainLayout {...props} />
    </ThemeProvider>
  );
}
